import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../firebase/UserProvider";
import { listTasks, listTaskUsers } from "../../firebase/tasks";
import { getPriorityBadgeClass, getStatusBadgeClass, formatFirestoreDate, PRIORITY_WEIGHT } from "../../utils/taskHelpers";

const TABLE_COLUMNS = [
  { key: "taskNumber", label: "# Task", cls: "w-28 pl-6" }, { key: "title", label: "Title" },
  { key: "assignee", label: "Assignee" }, { key: "priority", label: "Priority" },
  { key: "status", label: "Status" }, { key: "dueDate", label: "Due Date" },
  { key: "createdAt", label: "Created", cls: "pr-6" },
];

export function TasksRaisedByMe() {
  const { user } = useSession();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("taskNumber");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [u, t] = await Promise.all([listTaskUsers(), listTasks({ createdBy: user.uid })]);
        setUsers(u);
        setTasks(t);
      } catch (err) {
        setError(err.message || "Failed to load tasks.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const getAssigneeName = useCallback((uid) => {
    if (!uid || uid === "unassigned") return "Unassigned";
    const found = users.find((u) => u.uid === uid);
    return found ? found.name : "Unknown User";
  }, [users]);

  const assigneeOptions = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      const key = t.assigneeUid || "unassigned";
      if (!map.has(key)) map.set(key, { uid: key, name: getAssigneeName(t.assigneeUid), count: 0 });
      map.get(key).count += 1;
    });
    const list = Array.from(map.values()).sort((a, b) => (a.uid === "unassigned" ? 1 : b.uid === "unassigned" ? -1 : a.name.localeCompare(b.name)));
    return [{ uid: "All", name: "All", count: tasks.length }, ...list];
  }, [tasks, getAssigneeName]);

  useEffect(() => {
    if (activeFilter !== "All" && !assigneeOptions.some((opt) => opt.uid === activeFilter)) {
      setActiveFilter("All");
    }
  }, [assigneeOptions, activeFilter]);

  if (!user) return <div className="p-8 text-center font-bold">Please sign in to view your tasks.</div>;

  const handleSortToggle = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const getTime = (val) => (val?.toDate ? val.toDate().getTime() : new Date(val || 0).getTime());

  const filteredTasks = activeFilter === "All"
    ? tasks
    : tasks.filter((t) => (t.assigneeUid || "unassigned") === activeFilter);

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "taskNumber") cmp = (a.taskNumber || 0) - (b.taskNumber || 0);
    else if (sortBy === "title") cmp = (a.title || "").localeCompare(b.title || "");
    else if (sortBy === "assignee") cmp = getAssigneeName(a.assigneeUid).localeCompare(getAssigneeName(b.assigneeUid));
    else if (sortBy === "priority") cmp = (PRIORITY_WEIGHT[a.priority] || 0) - (PRIORITY_WEIGHT[b.priority] || 0);
    else if (sortBy === "status") cmp = (a.status || "").localeCompare(b.status || "");
    else if (sortBy === "dueDate") cmp = (a.dueDate ? new Date(a.dueDate).getTime() : 0) - (b.dueDate ? new Date(b.dueDate).getTime() : 0);
    else if (sortBy === "createdAt") cmp = getTime(a.createdAt) - getTime(b.createdAt);
    return sortOrder === "asc" ? cmp : -cmp;
  });

  return (
    <div className="container-fluid min-h-screen py-8 px-4 md:px-8" style={{ backgroundColor: "var(--theme-color-canvas)" }}>
      <div className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center" style={{ borderBottom: "1px solid var(--theme-color-mute)" }}>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}>
            Tasks Raised by Me
          </h1>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            All tasks you have created, filterable by assignee.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link to="/tasks" className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline" style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body)", borderColor: "var(--theme-color-mute)" }}>
            ← Task Inbox
          </Link>
        </div>
      </div>

      <div className="rounded-2xl border shadow-sm p-6 mb-8" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
        <label className="block text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--theme-color-body-mid)" }}>Filter by Assignee</label>
        <div className="flex flex-wrap gap-3">
          {assigneeOptions.map((opt) => {
            const isActive = activeFilter === opt.uid;
            const pillStyle = isActive
              ? { backgroundColor: "var(--theme-button-primary-bg)", color: "var(--theme-button-primary-text)", borderColor: "var(--theme-button-primary-bg)" }
              : { backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body)", borderColor: "var(--theme-color-mute)" };
            return (
              <button key={opt.uid} type="button" onClick={() => setActiveFilter(opt.uid)} className="px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 cursor-pointer" style={pillStyle}>
                {opt.name} ({opt.count})
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="p-4 mb-8 rounded-lg border font-semibold text-red-700 bg-red-50 border-red-200">{error}</div>}

      {loading ? (
        <div className="rounded-2xl border shadow-sm p-6 animate-pulse" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="h-8 bg-gray-200 rounded mb-4"></div><div className="h-8 bg-gray-200 rounded mb-4"></div><div className="h-8 bg-gray-200 rounded"></div>
        </div>
      ) : sortedTasks.length === 0 ? (
        <div className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body-mid)" }}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2"></path></svg>
          </div>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>No Tasks Found</h3>
          <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--theme-color-body)" }}>You haven't created any tasks yet, or none match the selected filter.</p>
        </div>
      ) : (
        <>
          <div className="hidden lg:block rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="text-xs uppercase tracking-wider bg-gray-50/50" style={{ color: "var(--theme-color-body-mid)", borderBottom: "1px solid var(--theme-color-canvas-soft)" }}>
                  <tr>
                    {TABLE_COLUMNS.map((col) => (
                      <th key={col.key} className={`p-4 cursor-pointer select-none ${col.cls || ""}`} onClick={() => handleSortToggle(col.key)}>
                        {col.label}{sortBy === col.key ? (sortOrder === "asc" ? " ▴" : " ▾") : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {sortedTasks.map((task) => (
                    <tr key={task.id} className="transition-colors hover:bg-slate-50/50" style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}>
                      <td className="p-4 pl-6 font-semibold"><Link to={`/tasks/${task.taskNumber}`} className="text-blue-600 hover:underline">#{task.taskNumber}</Link></td>
                      <td className="p-4 font-bold" style={{ color: "var(--theme-color-ink)" }}>{task.title}</td>
                      <td className="p-4" style={{ color: "var(--theme-color-ink)" }}>{getAssigneeName(task.assigneeUid)}</td>
                      <td className="p-4"><span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span></td>
                      <td className="p-4"><span className={getStatusBadgeClass(task.status)}>{task.status}</span></td>
                      <td className="p-4" style={{ color: "var(--theme-color-ink)" }}>{task.dueDate || "—"}</td>
                      <td className="p-4 pr-6" style={{ color: "var(--theme-color-body)" }}>{formatFirestoreDate(task.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="lg:hidden space-y-4">
            {sortedTasks.map((task) => (
              <div key={task.id} className="rounded-2xl border shadow-sm p-5 space-y-3" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
                <div className="flex justify-between items-center">
                  <Link to={`/tasks/${task.taskNumber}`} className="text-xs font-extrabold hover:underline px-2.5 py-1 rounded" style={{ color: "var(--theme-color-primary)", backgroundColor: "var(--theme-color-canvas-soft)" }}>#{task.taskNumber}</Link>
                  <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                </div>
                <h3 className="text-base font-bold m-0 line-clamp-2" style={{ color: "var(--theme-color-ink)" }}>{task.title}</h3>
                <div className="pt-2 flex justify-between items-center text-xs" style={{ borderTop: "1px solid var(--theme-color-canvas-soft)" }}>
                  <div><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Assignee</span><span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>{getAssigneeName(task.assigneeUid)}</span></div>
                  <div className="text-right"><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Due Date</span><span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>{task.dueDate || "—"}</span></div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <div><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Priority</span><span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span></div>
                  <div className="text-right"><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Created</span><span className="font-semibold" style={{ color: "var(--theme-color-body)" }}>{formatFirestoreDate(task.createdAt)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default TasksRaisedByMe;
